import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { ObjectivePeriod } from '@/types/objective-periods';
import type { ObjectivePeriodSummary, ObjectiveWindow } from '@/types/objectives';

import { useGetObjectivePeriods } from '@/api/objective-periods';
import {
    ALL_PERIODS_OPTION_ID,
    CUSTOM_DATES_OPTION_ID,
    FROM_PARAM,
    PAGE_PARAM,
    PERIOD_PARAM,
    TO_PARAM,
    type PeriodDateRange,
} from '@/constants/ObjectivePeriodLens';
import { ObjectivePeriodStatus } from '@/constants/ObjectivePeriodStatus';
import { setOrDelete, useReplaceSearch } from '@/hooks/utils/useReplaceSearch';
import { toIsoDate } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

/** One objective period, flattened into what a picker needs: a label and its two bounds. */
export interface PeriodOption {
    id: string;
    /** The period's name in the reader's language. */
    label: string;
    from: string;
    to: string;
    status: ObjectivePeriodStatusType;
}

/** Accepts both the administration's full period and the summary served beside an objective. */
type PeriodLike = ObjectivePeriod | ObjectivePeriodSummary;

const toPeriodOption = (period: PeriodLike): PeriodOption => ({
    id: period.id,
    label: getLocalizedText(period.name),
    from: period.startDate,
    to: period.endDate,
    status: period.status,
});

/** Reads the status alone, so it applies to a raw period and to a built option alike. */
const isActive = (period: Pick<PeriodOption, 'status'>): boolean =>
    period.status === ObjectivePeriodStatus.ACTIVE;

/** The option that starts last: the list's order is the server's, not something to rely on here. */
const mostRecent = (options: PeriodOption[]): PeriodOption | undefined =>
    options.reduce<PeriodOption | undefined>(
        (latest, option) => (!latest || option.from > latest.from ? option : latest),
        undefined,
    );

/** The option that starts first. */
const earliest = (options: PeriodOption[]): PeriodOption | undefined =>
    options.reduce<PeriodOption | undefined>(
        (first, option) => (!first || option.from < first.from ? option : first),
        undefined,
    );

/** A new objective is proposed the current period only while more than this many days remain in it. */
const MIN_DAYS_LEFT_FOR_NEW_OBJECTIVE = 20;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole calendar days from one ISO date to a later one: the 1st to the 21st is 20 days. */
const daysBetween = (earlierIsoDate: string, laterIsoDate: string): number =>
    Math.round((Date.parse(laterIsoDate) - Date.parse(earlierIsoDate)) / MS_PER_DAY);

const isCurrent = (option: PeriodOption, today: string): boolean =>
    option.from <= today && today <= option.to;

/**
 * Which period a page opens on: the active period today falls in, else the most recent active
 * one, else simply the first option offered. `undefined` when there is no period at all -- the
 * pages then list every objective.
 */
export const pickDefaultPeriodId = (options: PeriodOption[]): string | undefined => {
    const today = toIsoDate(new Date());
    const activeOptions = options.filter(isActive);
    const current = activeOptions.find((option) => isCurrent(option, today));

    return (current ?? mostRecent(activeOptions) ?? options[0])?.id;
};

/**
 * Which period a new objective is proposed: the current period while more than
 * `MIN_DAYS_LEFT_FOR_NEW_OBJECTIVE` days remain in it; closer to its end, the next active period to
 * start. Without either, the pages' default. The lists keep opening on the current period, where
 * the objectives still being wrapped up are.
 */
export const pickNewObjectivePeriodId = (options: PeriodOption[]): string | undefined => {
    const today = toIsoDate(new Date());
    const activeOptions = options.filter(isActive);
    const lastingCurrent = activeOptions.find(
        (option) =>
            isCurrent(option, today) &&
            daysBetween(today, option.to) > MIN_DAYS_LEFT_FOR_NEW_OBJECTIVE,
    );
    const next = earliest(activeOptions.filter((option) => option.from > today));

    return lastingCurrent?.id ?? next?.id ?? pickDefaultPeriodId(options);
};

interface PeriodOptionsResult {
    options: PeriodOption[];
    defaultPeriodId: string | undefined;
    isLoading: boolean;
    /** The period request failed: an empty list then means "unknown", not "no period". */
    isError: boolean;
}

/**
 * The period list and the language its labels are read in -- what both hooks below build on.
 * `getLocalizedText` resolves the name against the i18n singleton, so a language switch has to
 * re-derive the options; the language itself is the dependency, never `t` (which gets a new
 * identity on every render -- see unstable-t-translation-hook).
 */
const useObjectivePeriodList = () => {
    const {
        i18n: { language },
    } = useTranslation();
    const query = useGetObjectivePeriods();

    return { periods: query.data, language, isLoading: query.isLoading, isError: query.isError };
};

/**
 * The period lens of the objectives and pillar pages: every period an objective can be filed
 * under, drafts excluded -- a draft has never been opened to objectives, so filtering on it could
 * only ever return the free-dates objectives that happen to overlap it.
 *
 * With no such period at all, the lens opens on "all periods": that is what the lists then read,
 * and the control says so rather than asking for a period there is none of. Not before the list has
 * answered, though: an empty list on its way, or one that failed, proves nothing.
 */
export const usePeriodOptions = (): PeriodOptionsResult => {
    const { periods, language, isLoading, isError } = useObjectivePeriodList();

    const options = useMemo<PeriodOption[]>(
        () =>
            (periods ?? [])
                .filter((period) => period.status !== ObjectivePeriodStatus.DRAFT)
                .map(toPeriodOption),
        [periods, language],
    );
    const isKnown = !isLoading && !isError;
    const defaultPeriodId =
        pickDefaultPeriodId(options) ?? (isKnown ? ALL_PERIODS_OPTION_ID : undefined);

    return { options, defaultPeriodId, isLoading, isError };
};

/**
 * The periods an objective may be attached to: the active ones, plus -- when editing -- the one it
 * already carries, so a period since deactivated or completed stays selected and readable instead
 * of silently emptying the picker.
 */
export const useObjectiveFormPeriodOptions = (
    currentPeriod?: ObjectivePeriodSummary | null,
): PeriodOptionsResult => {
    const { periods, language, isLoading, isError } = useObjectivePeriodList();

    const options = useMemo<PeriodOption[]>(() => {
        const activeOptions = (periods ?? []).filter(isActive).map(toPeriodOption);

        return currentPeriod && !activeOptions.some((option) => option.id === currentPeriod.id)
            ? [toPeriodOption(currentPeriod), ...activeOptions]
            : activeOptions;
    }, [periods, currentPeriod, language]);

    return { options, defaultPeriodId: pickNewObjectivePeriodId(options), isLoading, isError };
};

/**
 * Reads the raw `?period=` value from a `location.search` string, or `undefined` if absent.
 * Shared with pages that only need to forward the current period onto a link (e.g. the
 * objective detail page's pillar link) without validating it against a known option list --
 * that validation is `usePeriodParam`'s job.
 */
export const readPeriodParam = (search: string): string | undefined =>
    new URLSearchParams(search).get(PERIOD_PARAM) ?? undefined;

const EMPTY_RANGE: PeriodDateRange = { from: '', to: '' };

const readRangeParams = (search: string): PeriodDateRange => {
    const params = new URLSearchParams(search);
    return { from: params.get(FROM_PARAM) ?? '', to: params.get(TO_PARAM) ?? '' };
};

interface PeriodParamOptions {
    /**
     * Lets `?period=custom-dates` stand for a range the reader picks, read from `?from=`/`?to=`.
     * Without it an unknown id falls back as before, so the screens that only know cycles are
     * never left on a lens they cannot show.
     */
    allowCustomDates?: boolean;
}

/**
 * Reads and writes the period lens from the query string, shared between the objectives home page,
 * a pillar's page and the employee portal: a cycle in `?period=`, `all-periods` for no window at
 * all, or -- where custom dates are offered -- a start and an end in `?from=`/`?to=`. Unknown or
 * missing ids fall back to `fallbackId` without rewriting the URL; an empty string stands for "no
 * period", which the pages read as an unfiltered list, exactly as `all-periods` does.
 *
 * `window` is the one thing the lists and the pillar totals are asked for, so the control and what
 * it filters can never drift apart. A custom range only narrows anything once **both** bounds are
 * picked: half a range would filter on something the reader never chose.
 */
export const usePeriodParam = (
    options: PeriodOption[],
    fallbackId?: string,
    { allowCustomDates = false }: PeriodParamOptions = {},
) => {
    const location = useLocation();
    const replaceSearch = useReplaceSearch();

    const periodId = useMemo(() => {
        const requested = readPeriodParam(location.search);
        if (requested && options.some((option) => option.id === requested)) {
            return requested;
        }

        if (requested === ALL_PERIODS_OPTION_ID) {
            return ALL_PERIODS_OPTION_ID;
        }

        if (allowCustomDates && requested === CUSTOM_DATES_OPTION_ID) {
            return CUSTOM_DATES_OPTION_ID;
        }

        return fallbackId ?? '';
    }, [location.search, options, fallbackId, allowCustomDates]);

    const isAllPeriods = periodId === ALL_PERIODS_OPTION_ID;
    const isCustomDates = periodId === CUSTOM_DATES_OPTION_ID;
    const customRange = useMemo(
        () => (isCustomDates ? readRangeParams(location.search) : EMPTY_RANGE),
        [isCustomDates, location.search],
    );

    const setPeriodId = useCallback(
        (nextId: string) => {
            replaceSearch((params) => {
                params.set(PERIOD_PARAM, nextId);
                params.delete(PAGE_PARAM);
                // A cycle carries its own bounds: leaving the custom range behind would filter on
                // dates no longer shown anywhere.
                if (nextId !== CUSTOM_DATES_OPTION_ID) {
                    params.delete(FROM_PARAM);
                    params.delete(TO_PARAM);
                }
            });
        },
        [replaceSearch],
    );

    const setCustomRange = useCallback(
        (range: PeriodDateRange) => {
            replaceSearch((params) => {
                params.set(PERIOD_PARAM, CUSTOM_DATES_OPTION_ID);
                params.delete(PAGE_PARAM);
                setOrDelete(params, FROM_PARAM, range.from);
                setOrDelete(params, TO_PARAM, range.to);
            });
        },
        [replaceSearch],
    );

    const window = useMemo<ObjectiveWindow>(() => {
        if (isAllPeriods) {
            return {};
        }

        if (isCustomDates) {
            return customRange.from && customRange.to
                ? { from: customRange.from, to: customRange.to }
                : {};
        }

        return periodId ? { objectivePeriodId: periodId } : {};
    }, [isAllPeriods, isCustomDates, customRange, periodId]);

    return {
        periodId,
        setPeriodId,
        isAllPeriods,
        isCustomDates,
        customRange,
        setCustomRange,
        window,
    };
};
